import { useTranslation } from 'react-i18next';

/**
 * Spec 525: session-status dot (Teams/Slack style) computed from the last
 * activity timestamp. Thresholds: active < 5 min, idle 5–30 min, offline > 30
 * min or never. Colour is not the only signal — the status is exposed via
 * `title` (hover) and an sr-only label (screen readers).
 */

const ACTIVE_MS = 5 * 60 * 1000;
const IDLE_MS = 30 * 60 * 1000;

export type PresenceStatus = 'active' | 'idle' | 'offline';

export function computePresenceStatus(
  lastActivityAt: string | null | undefined,
  nowMs: number = Date.now(),
): PresenceStatus {
  if (!lastActivityAt) return 'offline';
  const then = new Date(lastActivityAt).getTime();
  if (Number.isNaN(then)) return 'offline';
  const delta = nowMs - then;
  if (delta < ACTIVE_MS) return 'active';
  if (delta < IDLE_MS) return 'idle';
  return 'offline';
}

const DOT_CLASS: Record<PresenceStatus, string> = {
  active: 'bg-green-500',
  idle: 'bg-yellow-500',
  offline: 'bg-muted-foreground/40',
};

export function PresenceDot({ lastActivityAt }: { lastActivityAt: string | null | undefined }) {
  const { t } = useTranslation('users');
  const status = computePresenceStatus(lastActivityAt);
  const label = t(`presence.status.${status}`);

  // role="img" + aria-label gives an explicit accessible name (colour is not the
  // only signal); title provides the hover tooltip; the dot itself is decorative.
  return (
    <span className="inline-flex items-center" role="img" aria-label={label} title={label}>
      <span
        className={`inline-block h-2.5 w-2.5 rounded-full ${DOT_CLASS[status]}`}
        aria-hidden="true"
      />
    </span>
  );
}
